import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CopyIcon, EyeIcon, EyeOffIcon, GlobeIcon, WifiIcon, CreditCardIcon, SmartphoneIcon, ShieldAlertIcon, Edit2Icon, LockIcon, UnlockIcon, FileTextIcon, CheckIcon, TruckIcon, MapPinIcon, MailIcon } from 'lucide-react';
import { TopBar } from '../components/TopBar';
interface CardSettingsProps {
  navigate: (screen: string) => void;
}
type Step = 'empty' | 'delivery-method' | 'address' | 'quote' | 'pin' | 'confirm' | 'creating' | 'success';
export function CardSettings({
  navigate
}: CardSettingsProps) {
  const [hasCard, setHasCard] = useState(false);
  const [step, setStep] = useState<Step>('empty');
  // Flow state
  const [deliveryMethod, setDeliveryMethod] = useState<'delivery' | 'pickup' | null>(null);
  const [address, setAddress] = useState('');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  // Card state
  const [showCvv, setShowCvv] = useState(false);
  const [isCardFrozen, setIsCardFrozen] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [showCopied, setShowCopied] = useState(false);
  const [toggles, setToggles] = useState({
    online: true,
    atm: true,
    pos: true,
    contactless: false,
    international: false
  });
  const handleToggle = (key: keyof typeof toggles) => {
    setToggles(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  const handleViewPin = () => {
    setShowPin(true);
    setTimeout(() => setShowPin(false), 3000);
  };
  const handleCopyNumber = () => {
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 2000);
  };
  const ToggleSwitch = ({
    active
  }: {
    active: boolean;
  }) => <div className={`w-11 h-6 rounded-full relative shadow-inner transition-colors ${active ? 'bg-[#2BA89D]' : 'bg-gray-300'}`} data-id="element-984">
      <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm transition-all ${active ? 'right-1' : 'left-1'}`} data-id="element-985"></div>
    </div>;
  const handleNext = () => {
    switch (step) {
      case 'empty':
        setStep('delivery-method');
        break;
      case 'delivery-method':
        if (deliveryMethod) setStep('address');
        break;
      case 'address':
        if (deliveryMethod === 'pickup' || deliveryMethod === 'delivery' && address.length > 5) {
          setStep('quote');
        }
        break;
      case 'quote':
        setStep('pin');
        break;
      case 'pin':
        if (pin.length === 4) setStep('confirm');
        break;
      case 'confirm':
        if (pin === confirmPin) {
          setStep('creating');
          setTimeout(() => {
            setStep('success');
          }, 2000);
        }
        break;
      case 'success':
        setHasCard(true);
        break;
    }
  };
  const handleBack = () => {
    switch (step) {
      case 'empty':
        navigate('wallet');
        break;
      case 'delivery-method':
        setStep('empty');
        break;
      case 'address':
        setStep('delivery-method');
        break;
      case 'quote':
        setStep('address');
        break;
      case 'pin':
        setStep('quote');
        break;
      case 'confirm':
        setStep('pin');
        setConfirmPin('');
        break;
      default:
        navigate('wallet');
    }
  };
  const cardFee = 1000;
  const deliveryFee = deliveryMethod === 'delivery' ? 1500 : 0;
  const totalFee = cardFee + deliveryFee;
  if (!hasCard) {
    return <motion.div initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} exit={{
      opacity: 0,
      x: -20
    }} className="flex-1 overflow-y-auto pb-safe bg-slate-50 flex flex-col" data-id="element-986">
        <TopBar title="Request Card" showBack onBack={handleBack} data-id="element-987" />

        <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 text-center" data-id="element-988">
          <AnimatePresence mode="wait" data-id="element-989">
            {step === 'empty' && <motion.div key="empty" initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} className="flex flex-col items-center w-full max-w-xs" data-id="element-990">
                <div className="w-24 h-24 bg-emerald-50 rounded-full flex items-center justify-center mb-6 border-4 border-emerald-100" data-id="element-991">
                  <CreditCardIcon className="w-10 h-10 text-emerald-500" data-id="element-992" />
                </div>
                <h2 className="text-2xl font-black text-[#0D3B3B] mb-3" data-id="element-993">
                  Get Your Skydd Card
                </h2>
                <p className="text-sm text-gray-500 mb-8 leading-relaxed" data-id="element-994">
                  Request a physical card to make secure payments anywhere in
                  the world.
                </p>
                <button onClick={handleNext} className="w-full bg-[#0D3B3B] hover:bg-[#092D2D] text-[#FFD700] font-bold py-4 px-8 rounded-xl transition-all shadow-lg shadow-[#0D3B3B]/20 active:scale-95 text-lg" data-id="element-995">
                  Request Card
                </button>
              </motion.div>}

            {step === 'delivery-method' && <motion.div key="delivery-method" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="w-full max-w-xs" data-id="element-996">
                <h2 className="text-2xl font-black text-[#0D3B3B] mb-2" data-id="element-997">
                  How to get it?
                </h2>
                <p className="text-sm text-gray-500 mb-8" data-id="element-998">
                  Choose how you want to receive your new card.
                </p>

                <div className="space-y-4 mb-8" data-id="element-999">
                  <button onClick={() => setDeliveryMethod('delivery')} className={`w-full p-5 rounded-2xl border-2 flex items-center gap-4 transition-all text-left ${deliveryMethod === 'delivery' ? 'border-[#2BA89D] bg-[#2BA89D]/5' : 'border-gray-100 bg-white hover:border-gray-200'}`} data-id="element-1000">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${deliveryMethod === 'delivery' ? 'bg-[#2BA89D] text-white' : 'bg-gray-50 text-gray-400'}`} data-id="element-1001">
                      <TruckIcon className="w-6 h-6" data-id="element-1002" />
                    </div>
                    <div data-id="element-1003">
                      <h3 className="font-bold text-[#0D3B3B]" data-id="element-1004">
                        Home Delivery
                      </h3>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-1005">
                        Delivered in 3-5 business days
                      </p>
                    </div>
                  </button>

                  <button onClick={() => setDeliveryMethod('pickup')} className={`w-full p-5 rounded-2xl border-2 flex items-center gap-4 transition-all text-left ${deliveryMethod === 'pickup' ? 'border-[#2BA89D] bg-[#2BA89D]/5' : 'border-gray-100 bg-white hover:border-gray-200'}`} data-id="element-1006">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${deliveryMethod === 'pickup' ? 'bg-[#2BA89D] text-white' : 'bg-gray-50 text-gray-400'}`} data-id="element-1007">
                      <MapPinIcon className="w-6 h-6" data-id="element-1008" />
                    </div>
                    <div data-id="element-1009">
                      <h3 className="font-bold text-[#0D3B3B]" data-id="element-1010">Pick Up</h3>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-1011">
                        Collect from our partner locations
                      </p>
                    </div>
                  </button>
                </div>

                <button onClick={handleNext} disabled={!deliveryMethod} className="w-full bg-[#0D3B3B] disabled:bg-gray-200 disabled:text-gray-400 hover:bg-[#092D2D] text-[#FFD700] font-bold py-4 rounded-xl transition-all shadow-lg active:scale-95 text-lg" data-id="element-1012">
                  Continue
                </button>
              </motion.div>}

            {step === 'address' && <motion.div key="address" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="w-full max-w-xs text-left" data-id="element-1013">
                {deliveryMethod === 'delivery' ? <>
                    <h2 className="text-2xl font-black text-[#0D3B3B] mb-2 text-center" data-id="element-1014">
                      Delivery Address
                    </h2>
                    <p className="text-sm text-gray-500 mb-8 text-center" data-id="element-1015">
                      Where should we send your card?
                    </p>

                    <div className="mb-8" data-id="element-1016">
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1" data-id="element-1017">
                        Full Address
                      </label>
                      <textarea value={address} onChange={e => setAddress(e.target.value)} placeholder="Enter your full delivery address..." className="w-full bg-white border border-gray-200 rounded-2xl p-4 text-sm font-medium text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#2BA89D]/20 focus:border-[#2BA89D] transition-all min-h-[120px] resize-none" data-id="element-1018" />
                    </div>
                  </> : <>
                    <h2 className="text-2xl font-black text-[#0D3B3B] mb-2 text-center" data-id="element-1019">
                      Pickup Location
                    </h2>
                    <p className="text-sm text-gray-500 mb-8 text-center" data-id="element-1020">
                      Your card will be ready here.
                    </p>

                    <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-8" data-id="element-1021">
                      <div className="flex items-start gap-3" data-id="element-1022">
                        <MapPinIcon className="w-5 h-5 text-[#2BA89D] shrink-0 mt-0.5" data-id="element-1023" />
                        <div data-id="element-1024">
                          <h4 className="font-bold text-[#0D3B3B] mb-1" data-id="element-1025">
                            Skydd HQ
                          </h4>
                          <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1026">
                            14b Admiralty Way,
                            <br data-id="element-1027" />
                            Ikoyi, Lagos,
                            <br data-id="element-1028" />
                            Nigeria
                          </p>
                          <p className="text-xs text-gray-400 mt-3 font-medium" data-id="element-1029">
                            Open Mon-Fri, 9am - 5pm
                          </p>
                        </div>
                      </div>
                    </div>
                  </>}

                <button onClick={handleNext} disabled={deliveryMethod === 'delivery' && address.length < 5} className="w-full bg-[#0D3B3B] disabled:bg-gray-200 disabled:text-gray-400 hover:bg-[#092D2D] text-[#FFD700] font-bold py-4 rounded-xl transition-all shadow-lg active:scale-95 text-lg" data-id="element-1030">
                  Continue
                </button>
              </motion.div>}

            {step === 'quote' && <motion.div key="quote" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="w-full max-w-xs" data-id="element-1031">
                <h2 className="text-2xl font-black text-[#0D3B3B] mb-2" data-id="element-1032">
                  Payment Summary
                </h2>
                <p className="text-sm text-gray-500 mb-8" data-id="element-1033">
                  Review the charges for your card request.
                </p>

                <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 mb-8 text-left" data-id="element-1034">
                  <div className="space-y-4 mb-4 pb-4 border-b border-gray-100" data-id="element-1035">
                    <div className="flex justify-between items-center" data-id="element-1036">
                      <span className="text-sm text-gray-500" data-id="element-1037">
                        Card Issuance Fee
                      </span>
                      <span className="text-sm font-bold text-[#0D3B3B]" data-id="element-1038">
                        ₦1,000
                      </span>
                    </div>
                    <div className="flex justify-between items-center" data-id="element-1039">
                      <span className="text-sm text-gray-500" data-id="element-1040">
                        Delivery Fee
                      </span>
                      <span className="text-sm font-bold text-[#0D3B3B]" data-id="element-1041">
                        {deliveryFee === 0 ? 'Free' : `₦${deliveryFee.toLocaleString()}`}
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-1042">
                    <span className="font-bold text-[#0D3B3B]" data-id="element-1043">
                      Total to Pay
                    </span>
                    <span className="text-xl font-black text-[#2BA89D]" data-id="element-1044">
                      ₦{totalFee.toLocaleString()}
                    </span>
                  </div>
                </div>

                <button onClick={handleNext} className="w-full bg-[#0D3B3B] hover:bg-[#092D2D] text-[#FFD700] font-bold py-4 rounded-xl transition-all shadow-lg active:scale-95 text-lg" data-id="element-1045">
                  Pay ₦{totalFee.toLocaleString()}
                </button>
              </motion.div>}

            {(step === 'pin' || step === 'confirm') && <motion.div key="pin" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="w-full max-w-xs" data-id="element-1046">
                <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1047">
                  <LockIcon className="w-8 h-8 text-amber-500" data-id="element-1048" />
                </div>
                <h2 className="text-2xl font-black text-[#0D3B3B] mb-2" data-id="element-1049">
                  {step === 'pin' ? 'Set Card PIN' : 'Confirm PIN'}
                </h2>
                <p className="text-sm text-gray-500 mb-8" data-id="element-1050">
                  {step === 'pin' ? 'Create a 4-digit PIN to activate your card.' : 'Re-enter your PIN to confirm.'}
                </p>

                <input type="password" maxLength={4} value={step === 'pin' ? pin : confirmPin} onChange={e => step === 'pin' ? setPin(e.target.value) : setConfirmPin(e.target.value)} className="w-full bg-white border border-gray-200 rounded-2xl py-4 px-4 text-center tracking-[1em] text-2xl font-black text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#2BA89D]/20 focus:border-[#2BA89D] transition-all mb-6" placeholder="••••" autoFocus data-id="element-1051" />

                {step === 'confirm' && pin !== confirmPin && confirmPin.length === 4 && <p className="text-rose-500 text-xs font-bold mb-4" data-id="element-1052">
                      PINs do not match
                    </p>}

                <button onClick={handleNext} disabled={step === 'pin' && pin.length < 4 || step === 'confirm' && confirmPin.length < 4} className="w-full bg-[#0D3B3B] disabled:bg-gray-200 disabled:text-gray-400 hover:bg-[#092D2D] text-[#FFD700] font-bold py-4 rounded-xl transition-all shadow-lg active:scale-95 text-lg" data-id="element-1053">
                  Activate Card
                </button>
              </motion.div>}

            {step === 'creating' && <motion.div key="creating" initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} className="flex flex-col items-center" data-id="element-1054">
                <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mb-6 relative" data-id="element-1055">
                  <div className="absolute inset-0 border-4 border-emerald-200 border-t-emerald-500 rounded-full animate-spin" data-id="element-1056"></div>
                  <CreditCardIcon className="w-8 h-8 text-emerald-500" data-id="element-1057" />
                </div>
                <h2 className="text-xl font-black text-[#0D3B3B] mb-2" data-id="element-1058">
                  Processing...
                </h2>
                <p className="text-sm text-gray-500" data-id="element-1059">
                  Activating your new card
                </p>
              </motion.div>}

            {step === 'success' && <motion.div key="success" initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="flex flex-col items-center w-full max-w-xs" data-id="element-1060">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mb-6" data-id="element-1061">
                  <CheckIcon className="w-10 h-10 text-emerald-600" data-id="element-1062" />
                </div>
                <h2 className="text-2xl font-black text-[#0D3B3B] mb-3" data-id="element-1063">
                  Card Activated!
                </h2>
                <p className="text-sm text-gray-500 mb-6 leading-relaxed" data-id="element-1064">
                  Your card is ready to use. An email has been sent to you with
                  all the required information for card recovery.
                </p>
                <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 mb-8 flex items-start gap-3 text-left" data-id="element-1065">
                  <MailIcon className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" data-id="element-1066" />
                  <p className="text-xs text-blue-800 leading-relaxed" data-id="element-1067">
                    Check your inbox for your card recovery details and
                    important security information.
                  </p>
                </div>
                <button onClick={handleNext} className="w-full bg-[#0D3B3B] hover:bg-[#092D2D] text-[#FFD700] font-bold py-4 rounded-xl transition-all shadow-lg active:scale-95 text-lg" data-id="element-1068">
                  Go to Card
                </button>
              </motion.div>}
          </AnimatePresence>
        </div>
      </motion.div>;
  }
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="flex-1 overflow-y-auto pb-safe bg-slate-50" data-id="element-1069">
      <TopBar title="Cards" showBack onBack={() => navigate('wallet')} data-id="element-1070" />

      <div className="px-5 pt-4 pb-8" data-id="element-1071">
        {/* Virtual Card */}
        <div className="mb-6" data-id="element-1072">
          <div className="relative perspective-1000" data-id="element-1073">
            <motion.div animate={{
            rotateX: isCardFrozen ? 5 : 0,
            scale: isCardFrozen ? 0.98 : 1
          }} transition={{
            type: 'spring',
            damping: 20
          }} className={`relative rounded-3xl p-6 h-52 text-white shadow-2xl transition-all duration-500 overflow-hidden ${isCardFrozen ? 'bg-gray-400 shadow-none' : 'bg-gradient-to-br from-[#0D3B3B] via-[#092D2D] to-[#0D3B3B] shadow-emerald-900/30'}`} data-id="element-1074">
              {/* Decorative elements */}
              {!isCardFrozen && <img src="https://cdn.magicpatterns.com/uploads/cc1n38f9EanPNg5uP4yQAV/default_mascott_-_white.png" alt="Skydd Watermark" className="absolute top-2 right-2 w-28 h-28 object-contain opacity-[0.07] pointer-events-none" data-id="element-1075" />}

              {isCardFrozen && <div className="absolute inset-0 bg-white/30 backdrop-blur-md flex items-center justify-center z-20" data-id="element-1076">
                  <div className="bg-[#0D3B3B]/90 px-5 py-2.5 rounded-full flex items-center gap-2 shadow-xl" data-id="element-1077">
                    <LockIcon className="w-4 h-4 text-amber-400" data-id="element-1078" />
                    <span className="font-bold text-sm tracking-wide" data-id="element-1079">
                      Card Frozen
                    </span>
                  </div>
                </div>}

              <AnimatePresence data-id="element-1080">
                {showPin && !isCardFrozen && <motion.div initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.9
              }} className="absolute inset-0 bg-[#0D3B3B]/60 backdrop-blur-md flex items-center justify-center z-30 rounded-3xl" data-id="element-1081">
                    <div className="bg-white/10 border border-white/20 px-6 py-3 rounded-2xl flex flex-col items-center gap-1 shadow-2xl" data-id="element-1082">
                      <span className="text-xs font-bold text-white/70 uppercase tracking-widest" data-id="element-1083">
                        Your PIN
                      </span>
                      <span className="text-2xl font-mono font-bold tracking-[0.3em] text-white" data-id="element-1084">
                        ••1234
                      </span>
                    </div>
                  </motion.div>}
              </AnimatePresence>

              <div className={`relative z-10 h-full flex flex-col justify-between ${isCardFrozen ? 'opacity-40 grayscale' : ''}`} data-id="element-1085">
                <div className="flex justify-between items-start" data-id="element-1086">
                  <div className="font-bold text-xl tracking-[0.2em] text-white/90" data-id="element-1087">
                    SKYDD
                  </div>
                </div>

                <div data-id="element-1088">
                  <div className="text-2xl tracking-[0.15em] font-mono mb-3 text-white/90 drop-shadow-sm" data-id="element-1089">
                    **** **** **** 4092
                  </div>
                  <div className="flex justify-between items-center text-sm text-white/80 font-medium" data-id="element-1090">
                    <span className="uppercase tracking-widest text-xs" data-id="element-1091">
                      Adaeze Okonkwo
                    </span>
                    <span className="font-mono tracking-wider" data-id="element-1092">12/28</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Comprehensive Card Controls */}
          <div className="mt-6" data-id="element-1093">
            <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide px-1 -mx-1" data-id="element-1094">
              <button onClick={() => setIsCardFrozen(!isCardFrozen)} className="flex flex-col items-center gap-2 min-w-[72px] group" data-id="element-1095">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center shadow-sm transition-colors ${isCardFrozen ? 'bg-[#0D3B3B] text-white' : 'bg-white border border-gray-200 text-[#0D3B3B] group-hover:bg-gray-50'}`} data-id="element-1096">
                  {isCardFrozen ? <UnlockIcon className="w-5 h-5" data-id="element-1097" /> : <LockIcon className="w-5 h-5" data-id="element-1098" />}
                </div>
                <span className="text-[11px] font-bold text-[#0D3B3B] text-center leading-tight" data-id="element-1099">
                  {isCardFrozen ? 'Unfreeze' : 'Freeze'}
                </span>
              </button>

              <button onClick={handleViewPin} disabled={isCardFrozen} className="flex flex-col items-center gap-2 min-w-[72px] group disabled:opacity-50" data-id="element-1100">
                <div className="w-12 h-12 rounded-full bg-white border border-gray-200 text-[#0D3B3B] flex items-center justify-center shadow-sm group-hover:bg-gray-50 transition-colors" data-id="element-1101">
                  <EyeIcon className="w-5 h-5" data-id="element-1102" />
                </div>
                <span className="text-[11px] font-bold text-[#0D3B3B] text-center leading-tight" data-id="element-1103">
                  View PIN
                </span>
              </button>

              <button onClick={handleCopyNumber} className="flex flex-col items-center gap-2 min-w-[72px] group" data-id="element-1104">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center shadow-sm transition-colors ${showCopied ? 'bg-emerald-50 border border-emerald-200 text-emerald-600' : 'bg-white border border-gray-200 text-[#0D3B3B] group-hover:bg-gray-50'}`} data-id="element-1105">
                  {showCopied ? <CheckIcon className="w-5 h-5" data-id="element-1106" /> : <CopyIcon className="w-5 h-5" data-id="element-1107" />}
                </div>
                <span className={`text-[11px] font-bold text-center leading-tight ${showCopied ? 'text-emerald-600' : 'text-[#0D3B3B]'}`} data-id="element-1108">
                  {showCopied ? 'Copied!' : 'Copy No.'}
                </span>
              </button>

              <button className="flex flex-col items-center gap-2 min-w-[72px] group pr-4" data-id="element-1109">
                <div className="w-12 h-12 rounded-full bg-rose-50 border border-rose-100 text-rose-500 flex items-center justify-center shadow-sm group-hover:bg-rose-100 transition-colors" data-id="element-1110">
                  <ShieldAlertIcon className="w-5 h-5" data-id="element-1111" />
                </div>
                <span className="text-[11px] font-bold text-rose-600 text-center leading-tight" data-id="element-1112">
                  Block
                </span>
              </button>

              <button className="flex flex-col items-center gap-2 min-w-[72px] group pr-4" data-id="element-1113">
                <div className="w-12 h-12 rounded-full bg-white border border-gray-200 text-[#0D3B3B] flex items-center justify-center shadow-sm group-hover:bg-gray-50 transition-colors" data-id="element-1114">
                  <FileTextIcon className="w-5 h-5" data-id="element-1115" />
                </div>
                <span className="text-[11px] font-bold text-[#0D3B3B] text-center leading-tight" data-id="element-1116">
                  Statement
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Card Details */}
        <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-100 mb-6" data-id="element-1117">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4" data-id="element-1118">
            Card Details
          </h3>

          <div className="space-y-4" data-id="element-1119">
            <div className="flex justify-between items-center pb-4 border-b border-gray-50" data-id="element-1120">
              <div data-id="element-1121">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-1122">
                  Card Number
                </p>
                <p className="font-mono font-bold text-[#0D3B3B] tracking-wider" data-id="element-1123">
                  4092 1234 5678 9012
                </p>
              </div>
              <button className="p-2 bg-gray-50 rounded-lg text-gray-500 hover:text-[#0D3B3B] transition-colors" data-id="element-1124">
                <CopyIcon className="w-4 h-4" data-id="element-1125" />
              </button>
            </div>

            <div className="flex gap-6" data-id="element-1126">
              <div className="flex-1" data-id="element-1127">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-1128">
                  Expiry Date
                </p>
                <p className="font-mono font-bold text-[#0D3B3B]" data-id="element-1129">12/28</p>
              </div>
              <div className="flex-1" data-id="element-1130">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-1131">
                  CVV
                </p>
                <div className="flex items-center gap-3" data-id="element-1132">
                  <p className="font-mono font-bold text-[#0D3B3B]" data-id="element-1133">
                    {showCvv ? '482' : '***'}
                  </p>
                  <button onClick={() => setShowCvv(!showCvv)} className="text-gray-400 hover:text-[#0D3B3B]" data-id="element-1134">
                    {showCvv ? <EyeOffIcon className="w-4 h-4" data-id="element-1135" /> : <EyeIcon className="w-4 h-4" data-id="element-1136" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* PIN Management */}
        <div className="bg-white rounded-3xl p-2 shadow-sm border border-gray-100 mb-6 flex" data-id="element-1137">
          <button className="flex-1 py-3 text-sm font-bold text-[#0D3B3B] hover:bg-gray-50 rounded-2xl transition-colors" data-id="element-1138">
            View PIN
          </button>
          <div className="w-px bg-gray-100 my-2" data-id="element-1139"></div>
          <button className="flex-1 py-3 text-sm font-bold text-[#0D3B3B] hover:bg-gray-50 rounded-2xl transition-colors" data-id="element-1140">
            Change PIN
          </button>
        </div>

        {/* Transaction Controls */}
        <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-100 mb-6" data-id="element-1141">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4" data-id="element-1142">
            Transaction Controls
          </h3>

          <div className="space-y-1" data-id="element-1143">
            {[{
            id: 'online',
            icon: SmartphoneIcon,
            label: 'Online Transactions',
            desc: 'Websites and apps'
          }, {
            id: 'atm',
            icon: CreditCardIcon,
            label: 'ATM Withdrawals',
            desc: 'Cash from ATMs'
          }, {
            id: 'pos',
            icon: CreditCardIcon,
            label: 'POS Payments',
            desc: 'In-store purchases'
          }, {
            id: 'contactless',
            icon: WifiIcon,
            label: 'Contactless Payments',
            desc: 'Tap to pay'
          }, {
            id: 'international',
            icon: GlobeIcon,
            label: 'International',
            desc: 'Outside Nigeria'
          }].map((item, idx, arr) => <div key={item.id} className={`flex items-center justify-between py-3 ${idx !== arr.length - 1 ? 'border-b border-gray-50' : ''}`} data-id="element-1144">
                <div className="flex items-center gap-3" data-id="element-1145">
                  <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-500" data-id="element-1146">
                    <item.icon className="w-4 h-4" data-id="element-1147" />
                  </div>
                  <div data-id="element-1148">
                    <p className="text-sm font-bold text-[#0D3B3B]" data-id="element-1149">
                      {item.label}
                    </p>
                    <p className="text-[10px] font-medium text-gray-500" data-id="element-1150">
                      {item.desc}
                    </p>
                  </div>
                </div>
                <button onClick={() => handleToggle(item.id as keyof typeof toggles)} data-id="element-1151">
                  <ToggleSwitch active={toggles[item.id as keyof typeof toggles]} data-id="element-1152" />
                </button>
              </div>)}
          </div>
        </div>

        {/* Spending Limits */}
        <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-100 mb-6" data-id="element-1153">
          <div className="flex justify-between items-center mb-4" data-id="element-1154">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-1155">
              Spending Limits
            </h3>
            <button className="text-[#2BA89D] text-xs font-bold flex items-center gap-1" data-id="element-1156">
              <Edit2Icon className="w-3 h-3" data-id="element-1157" /> Edit
            </button>
          </div>

          <div className="space-y-4" data-id="element-1158">
            <div data-id="element-1159">
              <div className="flex justify-between text-sm mb-2" data-id="element-1160">
                <span className="font-bold text-[#0D3B3B]" data-id="element-1161">Daily Limit</span>
                <span className="font-medium text-gray-500" data-id="element-1162">
                  ₦45,000 / ₦100,000
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-1163">
                <div className="h-full bg-[#2BA89D] w-[45%] rounded-full" data-id="element-1164"></div>
              </div>
            </div>

            <div data-id="element-1165">
              <div className="flex justify-between text-sm mb-2" data-id="element-1166">
                <span className="font-bold text-[#0D3B3B]" data-id="element-1167">Monthly Limit</span>
                <span className="font-medium text-gray-500" data-id="element-1168">
                  ₦240,000 / ₦1,000,000
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-1169">
                <div className="h-full bg-[#FFD700] w-[24%] rounded-full" data-id="element-1170"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="bg-white rounded-3xl p-2 shadow-sm border border-gray-100" data-id="element-1171">
          <button className="w-full flex items-center gap-3 p-3 hover:bg-rose-50 rounded-2xl transition-colors text-rose-600" data-id="element-1172">
            <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center" data-id="element-1173">
              <ShieldAlertIcon className="w-4 h-4" data-id="element-1174" />
            </div>
            <div className="text-left" data-id="element-1175">
              <p className="text-sm font-bold" data-id="element-1176">Report Lost or Stolen</p>
              <p className="text-[10px] font-medium opacity-80" data-id="element-1177">
                Permanently block and replace card
              </p>
            </div>
          </button>
        </div>
      </div>
    </motion.div>;
}